import React, { FC, PropsWithChildren, useEffect, useRef, useState } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import useDebounce from '@hooks/useDebounce'
import useNavigateParams from '@hooks/useNavigateParams'
import useOutsideClick from '@hooks/useOutsideClick'
import SearchResults from '@layout/header/SearchResults'
import SearchService, {
  SearchCategory,
  SearchConfig,
  SuggestionResults,
} from '@services/SearchService'
import { isPresent } from '@utils/helpers'

const defaultConfig: SearchConfig = Object.freeze({
  [SearchCategory.YOUR_HOME]: 3,
  [SearchCategory.SCORE]: 3,
  [SearchCategory.GROUP]: 1,
  [SearchCategory.COMMUNITY_MEMBER]: 1,
})

type Props = PropsWithChildren<{
  searchConfig?: SearchConfig
  onShowResults: (show: boolean) => void
}>

const SearchBar: FC<Props> = ({ searchConfig = defaultConfig, onShowResults }) => {
  const { search, pathname } = useLocation()
  const { t } = useTranslation()
  const navigate = useNavigateParams()

  const [showResults, setShowResults] = useState(false)
  const [focused, setFocused] = useState(false)
  const [results, setResults] = useState<SuggestionResults | null>(null)
  const [numItemSelected, setNumItemSelected] = useState<number>(0)

  const [term, setTerm] = useState('')
  const [selectedValue, setSelectedValue] = useState({ term: '', category: '' })
  const debouncedTerm = useDebounce(term, 300)

  const searchRef = useOutsideClick<HTMLDivElement>(() => {
    setShowResults(false)
    setFocused(false)
  })

  const inputRef = useRef<HTMLInputElement | null>(null)
  const mounted = useRef(false)

  const clearTerm = () => {
    setSelectedValue({ term: '', category: '' })
    setTerm('')
    setNumItemSelected(0)
  }

  // Hide results when page changes (only necessary for React routing
  useEffect(() => {
    clearTerm()
    setFocused(false)
    setShowResults(false)
  }, [pathname, search])

  // Only call search when debounced term changes, not on initial page load
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
      return
    }

    SearchService.getSuggestions(debouncedTerm, searchConfig).then(([resp]) => {
      if (resp) {
        setNumItemSelected(0)
        setSelectedValue({ term: debouncedTerm, category: Object.keys(searchConfig)[0] })
        setResults(resp.data.results)
      }
    })
  }, [debouncedTerm, searchConfig])

  useEffect(() => {
    onShowResults(showResults && isPresent(term))
  }, [onShowResults, showResults, term])

  const onArrowPress = (dir: number, e: Event) => {
    // Up and down arrows normally change the cursor from the start and the end of the input field
    // We don't want that!
    e.preventDefault()

    // No term = no results
    if (term.length === 0) return

    const configKeys = Object.keys(searchConfig)
    const items = configKeys.reduce((arr, key) => {
      arr.push({ term, category: key })
      if (results && results[key]) {
        results[key].forEach((result) => {
          arr.push({ term: result.title, category: key })
        })
      }
      return arr
    }, [] as { term: string; category: string }[])

    const increment = numItemSelected + dir
    // If value ends up below zero start back at the bottom of the list
    const wrappedValue = increment < 0 ? items.length - 1 : increment
    // Use mod operator to continue wrap around the search suggestions
    const newValue = Math.abs(wrappedValue) % items.length

    if (newValue < items.length && newValue >= 0) {
      setNumItemSelected(newValue)
      const newTerm = items[newValue]
      if (newTerm) setSelectedValue(newTerm)
    }
  }

  const keyDownHandler = (e) => {
    switch (e.key) {
      case 'Enter': {
        SearchService.performSearch(
          selectedValue.category as SearchCategory,
          selectedValue.term,
          navigate
        )

        if (inputRef?.current) inputRef?.current.blur()
        clearTerm()
        break
      }
      case 'ArrowDown':
        onArrowPress(1, e)
        break
      case 'ArrowUp':
        onArrowPress(-1, e)
        break
      case 'Escape':
        clearTerm()
        break
      default:
        break
    }
    e.stopPropagation()
  }

  return (
    <>
      <div className="flex flex-1 flex-col justify-center sm:relative" ref={searchRef}>
        <div
          className={classNames(
            'sm:relative flex flex-row items-center justify-center sm:justify-start p-2.5 rounded lg:mx-4 mx-2 transition-colors ',
            {
              'bg-gray-highlight dark:bg-gray-highlight-dm': focused,
            }
          )}>
          <i className="nf-icon nf-search-lg text-gray-icons dark:text-gray-icons-dm text-xl" />
          <input
            type="text"
            inputMode="text"
            className="flex-1 ml-1.5 text-md desktop:text-base outline-none text-black dark:text-gray sm:max-w-full bg-transparent text-ellipsis"
            placeholder={t('palette.search')}
            onFocus={() => {
              setFocused(true)
              setShowResults(true)
            }}
            ref={inputRef}
            onBlur={() => clearTerm()}
            value={selectedValue.term}
            onChange={(e) => {
              setTerm(e.target.value)
              setSelectedValue({ term: e.target.value, category: Object.keys(searchConfig)[0] })
            }}
            onKeyDown={keyDownHandler}
          />

          {showResults && isPresent(debouncedTerm) && (
            <>
              <button
                type="button"
                onClick={() => setShowResults(false)}
                style={{ height: 'calc(100vh - 64px)' }}
                className="absolute sm:hidden left-0 top-full right-0 bg-black bg-opacity-20 z-50"
              />
              <SearchResults
                term={debouncedTerm}
                results={results}
                config={searchConfig}
                numItemSelected={numItemSelected}
              />
            </>
          )}
        </div>
      </div>
      {showResults && isPresent(term) && (
        <button type="button" className="sm:hidden mt-0.5 mr-1">
          <i className="nf-icon nf-close text-xl" />
        </button>
      )}
    </>
  )
}

export default SearchBar
